import { useState } from 'react'
import StatusChip from '../../components/ui/StatusChip'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  MenuItem,
  Alert,
} from '@mui/material'
import { Add as AddIcon } from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { format, subDays } from 'date-fns'
import {
  useAccountLedgerEntries,
  useAccountSummary,
  useCashCloses,
  useCloseCashDay,
  useRecordBankDeposit,
  useAddBankAccount,
  useReconcileOpening,
  useSetOpeningBalance,
} from '../../hooks/api/useAccountLedger'
import { formatCurrencyWithLocale as formatCurrency } from '../../utils/formatters'
import { LedgerEntry } from '../../services/accountLedger/accountLedgerService'

const REF_TYPE_LABELS: Record<string, string> = {
  opening_balance:       'Opening Balance',
  load_form_settlement:  'Settlement',
  credit_payment:        'Recovery',
  vendor_payment:        'Vendor Payment',
  expense:               'Expense',
  cash_close:            'Day Close Transfer',
  cash_variance:         'Cash Count Difference',
  bank_deposit:          'Bank Deposit',
  opening_reconciliation: 'Opening Reconciliation',
}

const AccountLedgerPage = () => {
  const [from, setFrom]   = useState<Date | null>(subDays(new Date(), 30))
  const [to, setTo]       = useState<Date | null>(new Date())
  const [page, setPage]   = useState(0)
  const [openDialog, setOpenDialog] = useState(false)
  const [openingAmount, setOpeningAmount] = useState('')
  const [account, setAccount] = useState('cash')
  const [closeOpen, setCloseOpen] = useState(false)
  const [counted, setCounted] = useState('')
  const [retained, setRetained] = useState('0')
  const [varianceReason, setVarianceReason] = useState('')
  const [depositOpen, setDepositOpen] = useState(false)
  const [depositAmounts, setDepositAmounts] = useState<Record<string, string>>({})
  const [depositBankId, setDepositBankId] = useState('')
  const [depositReference, setDepositReference] = useState('')
  const [depositDate, setDepositDate] = useState(format(new Date(), 'yyyy-MM-dd'))
  const [bankOpen, setBankOpen] = useState(false)
  const [bankName, setBankName] = useState('')
  const [reconcileOpen, setReconcileOpen] = useState(false)
  const [confirmedCash, setConfirmedCash] = useState('')
  const [confirmedTransit, setConfirmedTransit] = useState('0')
  const [confirmedBanks, setConfirmedBanks] = useState<Record<string, string>>({})
  const [reconcileReason, setReconcileReason] = useState('')

  const fmt = (d: Date | null) => (d ? format(d, 'yyyy-MM-dd') : undefined)

  const { data, isLoading } = useAccountLedgerEntries({
    from: fmt(from),
    to:   fmt(to),
    page: page + 1,
    account: account === 'all' ? undefined : account === 'cash' || account === 'in_transit' ? account : 'bank',
    bank_account_id: !['all', 'cash', 'in_transit'].includes(account) ? account : undefined,
  })

  const { data: summary, isLoading: balanceLoading } = useAccountSummary()
  const { data: closes = [] } = useCashCloses()
  const setOpeningMutation = useSetOpeningBalance()
  const closeMutation = useCloseCashDay()
  const depositMutation = useRecordBankDeposit()
  const bankMutation = useAddBankAccount()
  const reconcileMutation = useReconcileOpening()
  const pendingCloses = closes.filter((close) => close.remaining > 0)
  const allocations = pendingCloses.map((close) => ({ cash_close_id: close._id, amount: Number(depositAmounts[close._id] || 0) }))
    .filter((item) => item.amount > 0)
  const depositTotal = allocations.reduce((sum, item) => sum + item.amount, 0)
  const depositInvalid = allocations.length === 0 || pendingCloses.some((close) => {
    const raw = depositAmounts[close._id]
    if (!raw) return false
    const amount = Number(raw)
    return !Number.isFinite(amount) || amount < 0 || amount > close.remaining
      || Math.abs(Math.round(amount * 100) - amount * 100) > 0.000001
  })
  const countedNumber = Number(counted)
  const retainedNumber = Number(retained)
  const expected = summary?.cash ?? 0
  const difference = counted === '' ? 0 : Math.round((countedNumber - expected) * 100) / 100
  const alreadyClosedToday = summary?.latest_close?.business_date === format(new Date(), 'yyyy-MM-dd')
  const confirmedTotal = Number(confirmedCash) + Number(confirmedTransit)
    + (summary?.banks.reduce((sum, bank) => sum + Number(confirmedBanks[bank._id] || 0), 0) ?? 0)
  const openingDifference = Math.round((confirmedTotal - expected) * 100) / 100

  const handleReconcile = () => {
    const cash = Number(confirmedCash)
    const in_transit = Number(confirmedTransit)
    const banks = summary?.banks.map((bank) => ({ bank_account_id: bank._id, amount: Number(confirmedBanks[bank._id] || 0) })) ?? []
    if (!summary || confirmedCash === '' || !Number.isFinite(cash) || cash < 0
      || !Number.isFinite(in_transit) || in_transit < 0
      || banks.some((bank) => !Number.isFinite(bank.amount) || bank.amount < 0)
      || (openingDifference !== 0 && !reconcileReason.trim())) return
    reconcileMutation.mutate({ expected, cash, in_transit, banks, reason: reconcileReason.trim() }, {
      onSuccess: () => { setReconcileOpen(false); setConfirmedCash(''); setConfirmedTransit('0'); setConfirmedBanks({}); setReconcileReason('') },
    })
  }

  const handleCloseDay = () => {
    if (counted === '' || !Number.isFinite(countedNumber) || countedNumber < 0 || !Number.isFinite(retainedNumber)
      || retainedNumber < 0 || retainedNumber > countedNumber || (difference !== 0 && !varianceReason.trim())) return
    closeMutation.mutate({ expected, counted: countedNumber, retained: retainedNumber, variance_reason: varianceReason.trim() }, {
      onSuccess: () => { setCloseOpen(false); setCounted(''); setRetained('0'); setVarianceReason('') },
    })
  }

  const handleDeposit = () => {
    if (depositInvalid || !depositBankId || !depositReference.trim() || !depositDate) return
    depositMutation.mutate({ allocations, bank_account_id: depositBankId,
      reference: depositReference.trim(), deposited_at: depositDate }, {
      onSuccess: () => { setDepositOpen(false); setDepositAmounts({}); setDepositReference('') },
    })
  }

  const handleSetOpening = () => {
    const amount = parseFloat(openingAmount)
    if (isNaN(amount) || amount < 0) return
    setOpeningMutation.mutate(amount, {
      onSuccess: () => {
        setOpenDialog(false)
        setOpeningAmount('')
      },
    })
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h4">Account Ledger</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {!summary?.reconciled && <Button variant="contained" onClick={() => {
            setConfirmedCash(expected.toFixed(2)); setReconcileOpen(true)
          }} disabled={!summary}>Confirm Opening Balances</Button>}
          <Button variant="contained" onClick={() => setCloseOpen(true)} disabled={!summary?.reconciled || alreadyClosedToday || expected < 0}>
            Close Cash Day
          </Button>
          <Button variant="outlined" onClick={() => setDepositOpen(true)} disabled={pendingCloses.length === 0 || !summary?.banks.length}>
            Record Bank Deposit
          </Button>
          <Button variant="outlined" onClick={() => setBankOpen(true)}>Add Bank Account</Button>
          {summary && !summary.has_ledger_entries && !summary.reconciled && <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
          >
            Set Opening Balance
          </Button>}
          </Box>
        </Box>

        {/* Balance card */}
        <Paper sx={{ p: 3, mb: 2, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Cash in Hand
            </Typography>
            {balanceLoading ? (
              <CircularProgress size={20} />
            ) : (
              <Typography variant="h4" fontWeight="bold" color={expected >= 0 ? 'success.main' : 'error.main'}>
                {formatCurrency(expected)}
              </Typography>
            )}
          </Box>
          <Box><Typography variant="body2" color="text.secondary">Awaiting Deposit</Typography>
            <Typography variant="h5" fontWeight="bold">{formatCurrency(summary?.in_transit ?? 0)}</Typography></Box>
          {summary?.banks.map((bank) => <Box key={bank._id}><Typography variant="body2" color="text.secondary">{bank.name}</Typography>
            <Typography variant="h5" fontWeight="bold">{formatCurrency(bank.balance)}</Typography></Box>)}
        </Paper>
        {!summary?.reconciled && <Alert severity="info" sx={{ mb: 2 }}>
          Existing ledger entries are provisionally treated as cash. Confirm actual cash, transit and bank balances before the first day close.
        </Alert>}
        {summary?.latest_close && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Last cash close: {summary.latest_close.business_date} · Counted {formatCurrency(summary.latest_close.counted)}
        </Typography>}

        {pendingCloses.length > 0 && <Paper sx={{ p: 2, mb: 3 }}><Typography variant="h6" gutterBottom>Awaiting Bank Deposit</Typography>
          {pendingCloses.map((close) => <Box key={close._id} sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', py: 0.5 }}>
            <Typography>{close.kind === 'opening_transit' ? 'Opening transit' : close.business_date}</Typography><Typography>{formatCurrency(close.remaining)} remaining</Typography>
          </Box>)}
        </Paper>}

        {closes.length > 0 && <Paper sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ p: 2 }}>Recent Cash Closes</Typography>
          <TableContainer><Table size="small"><TableHead><TableRow>
            <TableCell>Date</TableCell><TableCell align="right">Expected</TableCell><TableCell align="right">Counted</TableCell>
            <TableCell align="right">Difference</TableCell><TableCell align="right">Handed Over</TableCell><TableCell align="right">Awaiting Deposit</TableCell>
          </TableRow></TableHead><TableBody>{closes.slice(0, 10).map((close) => <TableRow key={close._id}>
            <TableCell>{close.kind === 'opening_transit' ? 'Opening transit' : close.business_date}</TableCell><TableCell align="right">{formatCurrency(close.expected)}</TableCell>
            <TableCell align="right">{formatCurrency(close.counted)}</TableCell><TableCell align="right">{formatCurrency(close.variance)}</TableCell>
            <TableCell align="right">{formatCurrency(close.transferred)}</TableCell><TableCell align="right">{formatCurrency(close.remaining)}</TableCell>
          </TableRow>)}</TableBody></Table></TableContainer>
        </Paper>}

        {/* Date filter */}
        <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <DatePicker
            label="From"
            value={from}
            onChange={(v) => { setFrom(v); setPage(0) }}
            slotProps={{ textField: { size: 'small' } }}
          />
          <TextField select label="Account" size="small" value={account} onChange={(event) => { setAccount(event.target.value); setPage(0) }} sx={{ minWidth: 170 }}>
            <MenuItem value="cash">Cash in hand</MenuItem><MenuItem value="in_transit">Awaiting deposit</MenuItem>
            {summary?.banks.map((bank) => <MenuItem key={bank._id} value={bank._id}>{bank.name}</MenuItem>)}
            <MenuItem value="all">All accounts</MenuItem>
          </TextField>
          <DatePicker
            label="To"
            value={to}
            minDate={from ?? undefined}
            onChange={(v) => { setTo(v); setPage(0) }}
            slotProps={{ textField: { size: 'small' } }}
          />
        </Paper>

        {/* Entries table */}
        <Paper>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f7f5f2' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Account</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Account Balance After</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.results.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                          No entries in this period
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.results.map((entry: LedgerEntry) => (
                        <TableRow
                          key={entry._id}
                          hover
                          sx={{
                            bgcolor: 'background.paper',
                            '&:nth-of-type(even)': { bgcolor: 'secondary.main' },
                          }}
                        >
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {format(new Date(entry.date), 'dd MMM yyyy')}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{entry.description}</Typography>
                            <Typography variant="caption" color="text.disabled">
                              {REF_TYPE_LABELS[entry.ref_type] ?? entry.ref_type}
                            </Typography>
                          </TableCell>
                          <TableCell>{entry.account === 'bank'
                            ? summary?.banks.find((bank) => bank._id === entry.bank_account_id)?.name ?? 'Bank'
                            : entry.account === 'in_transit' ? 'Awaiting deposit' : 'Cash in hand'}</TableCell>
                          <TableCell>
                            <StatusChip
                              label={entry.type === 'credit' ? 'Credit' : 'Debit'}
                              variant={entry.type === 'credit' ? 'success' : 'danger'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              color={entry.type === 'credit' ? 'success.main' : 'error.main'}
                            >
                              {entry.type === 'credit' ? '+' : '-'}{formatCurrency(entry.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold">
                              {formatCurrency(entry.balance_after)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {data && data.info.total > 0 && (
                <TablePagination
                  component="div"
                  count={data.info.total}
                  page={page}
                  rowsPerPage={30}
                  rowsPerPageOptions={[30]}
                  onPageChange={(_, newPage) => setPage(newPage)}
                />
              )}
            </>
          )}
        </Paper>

        {/* Opening balance dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Set Opening Balance</DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This can only be set once per company. Enter the starting cash balance.
            </Typography>
            <TextField
              label="Amount (Rs)"
              type="number"
              fullWidth
              size="small"
              value={openingAmount}
              onChange={(e) => setOpeningAmount(e.target.value)}
              inputProps={{ min: 0 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSetOpening}
              disabled={!openingAmount || setOpeningMutation.isPending}
            >
              {setOpeningMutation.isPending ? <CircularProgress size={18} /> : 'Set Balance'}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={reconcileOpen} onClose={() => setReconcileOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Confirm Opening Balances</DialogTitle>
          <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2">Existing ledger balance: <strong>{formatCurrency(expected)}</strong></Typography>
            <TextField label="Actual cash in hand" type="number" value={confirmedCash} onChange={(event) => setConfirmedCash(event.target.value)}
              inputProps={{ min: 0, step: 0.01 }} fullWidth required />
            <TextField label="Cash already awaiting bank deposit" type="number" value={confirmedTransit}
              onChange={(event) => setConfirmedTransit(event.target.value)} inputProps={{ min: 0, step: 0.01 }} fullWidth />
            {summary?.banks.map((bank) => <TextField key={bank._id} label={`Opening balance · ${bank.name}`} type="number"
              value={confirmedBanks[bank._id] ?? '0'} onChange={(event) => setConfirmedBanks((prev) => ({ ...prev, [bank._id]: event.target.value }))}
              inputProps={{ min: 0, step: 0.01 }} fullWidth />)}
            <Alert severity={openingDifference === 0 ? 'success' : 'warning'}>
              Confirmed total: {formatCurrency(confirmedTotal)} · Opening adjustment: {formatCurrency(openingDifference)}
            </Alert>
            {openingDifference !== 0 && <TextField label="Reason for opening adjustment" value={reconcileReason}
              onChange={(event) => setReconcileReason(event.target.value)} fullWidth required multiline rows={2} />}
            <Typography variant="body2" color="text.secondary">This one-time entry records the difference and allocates confirmed bank balances. Review your historical figures before confirming.</Typography>
          </DialogContent>
          <DialogActions><Button onClick={() => setReconcileOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleReconcile} disabled={reconcileMutation.isPending || confirmedCash === ''
              || Number(confirmedCash) < 0 || Number(confirmedTransit) < 0 || (openingDifference !== 0 && !reconcileReason.trim())}>Confirm Balances</Button>
          </DialogActions>
        </Dialog>
        <Dialog open={closeOpen} onClose={() => setCloseOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Close Cash Day</DialogTitle>
          <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography>Expected cash: <strong>{formatCurrency(expected)}</strong></Typography>
            <TextField label="Actual cash counted" type="number" value={counted} onChange={(event) => setCounted(event.target.value)}
              inputProps={{ min: 0, step: 0.01 }} fullWidth required />
            <TextField label="Cash retained for next day" type="number" value={retained} onChange={(event) => setRetained(event.target.value)}
              inputProps={{ min: 0, step: 0.01 }} fullWidth />
            {counted !== '' && <Alert severity={difference === 0 ? 'success' : 'warning'}>
              Difference: {formatCurrency(difference)} · Awaiting deposit after close: {formatCurrency(Math.max(0, countedNumber - retainedNumber))}
            </Alert>}
            {difference !== 0 && <TextField label="Reason for difference" value={varianceReason}
              onChange={(event) => setVarianceReason(event.target.value)} required fullWidth multiline rows={2} />}
            <Typography variant="body2" color="text.secondary">This records the cash count and transfers the handed-over amount to Awaiting Deposit. Further cash entries for today will be blocked.</Typography>
          </DialogContent>
          <DialogActions><Button onClick={() => setCloseOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleCloseDay} disabled={closeMutation.isPending || counted === '' || countedNumber < 0
              || retainedNumber < 0 || retainedNumber > countedNumber || (difference !== 0 && !varianceReason.trim())}>
              {closeMutation.isPending ? 'Closing...' : 'Confirm Close'}
            </Button></DialogActions>
        </Dialog>

        <Dialog open={bankOpen} onClose={() => setBankOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Add Bank Account</DialogTitle>
          <DialogContent sx={{ pt: 3 }}><TextField label="Bank account name" value={bankName} onChange={(event) => setBankName(event.target.value)} fullWidth autoFocus sx={{ mt: 1 }} /></DialogContent>
          <DialogActions><Button onClick={() => setBankOpen(false)}>Cancel</Button>
            <Button variant="contained" disabled={!bankName.trim() || bankMutation.isPending} onClick={() => bankMutation.mutate(bankName.trim(), {
              onSuccess: () => { setBankOpen(false); setBankName('') },
            })}>Add Bank</Button></DialogActions>
        </Dialog>

        <Dialog open={depositOpen} onClose={() => setDepositOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Record Bank Deposit</DialogTitle>
          <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>Cash batches included in this deposit</Typography>
            {pendingCloses.map((close) => <TextField key={close._id} label={`${close.kind === 'opening_transit' ? 'Opening transit' : close.business_date} · ${formatCurrency(close.remaining)} available`}
              type="number" value={depositAmounts[close._id] ?? ''}
              onChange={(event) => setDepositAmounts((prev) => ({ ...prev, [close._id]: event.target.value }))}
              inputProps={{ min: 0, max: close.remaining, step: 0.01 }} fullWidth />)}
            <Typography variant="body2">Total deposit: <strong>{formatCurrency(depositTotal)}</strong></Typography>
            <TextField select label="Deposit into bank" value={depositBankId} onChange={(event) => setDepositBankId(event.target.value)} fullWidth required>
              {summary?.banks.map((bank) => <MenuItem key={bank._id} value={bank._id}>{bank.name}</MenuItem>)}
            </TextField>
            <TextField label="Bank deposit reference" value={depositReference} onChange={(event) => setDepositReference(event.target.value)} fullWidth required />
            <TextField label="Deposit date" type="date" value={depositDate} onChange={(event) => setDepositDate(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth required />
          </DialogContent>
          <DialogActions><Button onClick={() => setDepositOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleDeposit} disabled={depositMutation.isPending || depositInvalid || !depositBankId
              || !depositReference.trim()}>
              {depositMutation.isPending ? 'Recording...' : 'Record Deposit'}
            </Button></DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  )
}

export default AccountLedgerPage
